/* eslint-disable no-console */
import { useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { uploadFotoGaleria } from "../../utils";

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

const CIRCUITO_OPTIONS = [
  { value: "asc", label: "Ascensores" },
  { value: "cir", label: "Circulación" },
  { value: "esp", label: "Espera" },
  { value: "cow", label: "Cowork" },
  { value: "dir", label: "Directorios Digitales" },
];

const capitalizeFirst = (str) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const SubirFotoModal = ({ show, onHide, onUploadSuccess, edificiosList, clientesList, categorias, userId }) => {
  const [archivo, setArchivo] = useState(null);
  const [idcliente, setIdcliente] = useState("");
  const [idedificio, setIdedificio] = useState("");
  const [screenType, setScreenType] = useState("");
  const [fileError, setFileError] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const resetForm = () => {
    setArchivo(null);
    setIdcliente("");
    setIdedificio("");
    setScreenType("");
    setFileError("");
    setSaving(false);
    setSuccessMsg("");
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setArchivo(null);
      setFileError("");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setArchivo(null);
      setFileError("La imagen no puede superar 3 MB");
      e.target.value = "";
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setArchivo(null);
      setFileError("Solo se permiten imágenes JPG, PNG o WebP");
      e.target.value = "";
      return;
    }
    setArchivo(file);
    setFileError("");
  };

  const selectedCliente = useMemo(() => {
    if (!idcliente) return null;
    return clientesList.find((c) => c.id === Number(idcliente));
  }, [idcliente, clientesList]);

  const selectedEdificio = useMemo(() => {
    if (!idedificio) return null;
    return edificiosList.find((e) => e.id === Number(idedificio));
  }, [idedificio, edificiosList]);

  const derivedCategoria = useMemo(() => {
    if (!selectedCliente?.categoria || !categorias) return "";
    return capitalizeFirst(categorias[selectedCliente.categoria]) || "";
  }, [selectedCliente, categorias]);

  const derivedPais = useMemo(() => selectedEdificio?.pais || "", [selectedEdificio]);

  const canSave = archivo && idedificio && screenType && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await uploadFotoGaleria(archivo, {
        idcliente: idcliente ? Number(idcliente) : null,
        idedificio: Number(idedificio),
        screen_type: screenType,
        created_by: userId,
      });
      setSuccessMsg("Imagen cargada correctamente");
      setSaving(false);
      setTimeout(() => {
        resetForm();
        onUploadSuccess();
      }, 1500);
    } catch (error) {
      console.error("Error subiendo foto:", error);
      setFileError("Error al subir la foto. Intente nuevamente.");
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Subir Foto</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label fw-semibold">Imagen *</label>
          <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.webp" onChange={handleFileChange} />
          {fileError && <small className="text-danger">{fileError}</small>}
          {successMsg && (
            <div className="alert alert-success py-2 mt-2 mb-0" role="alert">
              {successMsg}
            </div>
          )}
          <small className="text-muted d-block mt-1">JPG, PNG o WebP. Máximo 3 MB.</small>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Edificio *</label>
          <select className="form-select" value={idedificio} onChange={(e) => setIdedificio(e.target.value)}>
            <option value="">Seleccione edificio</option>
            {edificiosList.map((e) => (
              <option key={e.id} value={e.id}>
                {e.edificio} {e.pais ? `(${e.pais})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Cliente</label>
          <select className="form-select" value={idcliente} onChange={(e) => setIdcliente(e.target.value)}>
            <option value="">Sin cliente</option>
            {clientesList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
                {c.pais ? `, ${c.pais}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Circuito *</label>
          <select className="form-select" value={screenType} onChange={(e) => setScreenType(e.target.value)}>
            <option value="">Seleccione circuito</option>
            {CIRCUITO_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {(derivedCategoria || derivedPais) && (
          <div className="p-2 bg-light rounded border mb-2" style={{ fontSize: "0.85rem" }}>
            {derivedPais && (
              <div>
                <strong>País:</strong> {derivedPais}
              </div>
            )}
            {derivedCategoria && (
              <div>
                <strong>Categoría:</strong> {derivedCategoria}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? "Subiendo..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubirFotoModal;
